// PollLibrarySmart.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Col,
    Form,
    Offcanvas,
    Row,
} from "react-bootstrap";
import { Context } from "../../index";
import PollLibraryItem from "../../components/poolpages/PollLibraryItem";
import { getPollItemsLibrary } from "../../http/poolAPI";
import { observer } from "mobx-react-lite";
import PagesPolls from "../../components/poolpages/pagesPolls";
import Loader from "../../components/Loader";
import AccountSuspendedAlert from "../../components/AccountSuspendedAlert";
import SEO from "../../components/seo/SEO";

const MobileFilterPanel = React.memo(function MobileFilterPanel({
                                                                    show,
                                                                    mobileDraft,
                                                                    setMobileDraft,
                                                                    pollTypes,
                                                                    languages,
                                                                    toggleInSet,
                                                                    onClose,         // закрыть без применения (крестик)
                                                                    onApply,         // применить и закрыть (Done)
                                                                    onResetApply,    // сбросить -> применить -> закрыть (Reset)
                                                                }) {
    if (!show) return null;

    return (
        <Offcanvas
            show
            placement="end"
            onHide={onClose}
            backdrop="static"
            keyboard={false}
            scroll
            autoFocus={false}
            restoreFocus={false}
            transition={false}   // <— отключаем анимацию => нет "передёргивания"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Filters</Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="gap-3 pt-2 pb-3">
                <div className="mb-3 d-md-none">
                    <h6 className="fw-semibold text-muted">Poll Library</h6>
                    <p className="text-muted small mb-1">Browse polls, join one, and earn rewards.</p>
                    <p className="text-muted small mb-1">Use filters to find the best match.</p>
                </div>

                {/* Poll Type */}
                <div className="mb-3">
                    <Form.Label className="small text-muted">Poll Type</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                        {pollTypes.map((p) => {
                            const active = mobileDraft?.types?.has(p.type);
                            return (
                                <Button
                                    key={p.id}
                                    size="sm"
                                    variant={active ? "success" : "outline-success"}
                                    onClick={() =>
                                        setMobileDraft(d => ({ ...d, types: toggleInSet(d.types, p.type) }))
                                    }
                                >
                                    {p.name}
                                </Button>
                            );
                        })}
                    </div>
                </div>

                {/* Language */}
                <div className="mb-3">
                    <Form.Label className="small text-muted">Language</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                        {languages.map((l) => {
                            const active = mobileDraft?.langs?.has(l.name);
                            return (
                                <Button
                                    key={l.id}
                                    size="sm"
                                    variant={active ? "success" : "outline-success"}
                                    onClick={() =>
                                        setMobileDraft(d => ({ ...d, langs: toggleInSet(d.langs, l.name) }))
                                    }
                                >
                                    {l.name}
                                </Button>
                            );
                        })}
                    </div>
                </div>

                {/* Sort */}
                <div className="mb-3">
                    <Row>
                        <Col>
                            <Form.Label className="small text-muted">Sort Polls by:</Form.Label>
                            <Form.Select
                                size="sm"
                                value={mobileDraft?.sort ?? "newest"}
                                onChange={(e) => setMobileDraft(d => ({ ...d, sort: e.target.value }))}
                            >
                                <option value="newest">Newest first</option>
                                <option value="earnings">Highest earnings first</option>
                                <option value="time">Longest time first</option>
                            </Form.Select>
                        </Col>
                        <Col>
                            <Form.Label className="small text-muted">Records Per Page:</Form.Label>
                            <Form.Select
                                size="sm"
                                value={mobileDraft?.limit ?? 48}
                                onChange={(e) => setMobileDraft(d => ({ ...d, limit: Number(e.target.value) }))}
                            >
                                <option value={12}>12</option>
                                <option value={24}>24</option>
                                <option value={48}>48</option>
                                <option value={96}>96</option>
                            </Form.Select>
                        </Col>
                    </Row>

                </div>

                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={onResetApply}>
                       Clear All & Close
                    </Button>
                    <Button variant="success" size="sm" onClick={onApply}>
                        Apply Filters
                    </Button>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
});

// простая локальная реализация debounce, чтобы не тянуть lodash
function debounce(fn, delay = 400) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), delay);
    };
}

function cloneFilters(f) {
    return {
        ...f,
        types: new Set(f.types),
        langs: new Set(f.langs),
    };
}

// утилита для работы с Set-фильтрами
function toggleInSet(prevSet, value) {
    const next = new Set(prevSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
}

function ActiveFilterChips({ filters, onClear, onRemove }) {
    const chips = [];

    // Отдельные «пипки» для типов
    if (filters.types?.size) {
        [...filters.types].forEach((t) =>
            chips.push({ key: `type:${t}`, label: `Type: ${t}` })
        );
    }

    // Отдельные «пипки» для языков
    if (filters.langs?.size) {
        [...filters.langs].forEach((l) =>
            chips.push({ key: `lang:${l}`, label: `Lang: ${l}` })
        );
    }

    // Поиск
    if (filters.query?.trim()) {
        chips.push({ key: "query", label: `Search: "${filters.query.trim()}"` });
    }

    // Сортировка
    if (filters.sort && filters.sort !== "newest") {
        chips.push({ key: `sort:${filters.sort}`, label: `Sort: ${filters.sort}` });
    }

    if (!chips.length) return null;

    return (
        <div className="d-flex flex-wrap align-items-center gap-2 ms-2 mb-3">
            {chips.map((chip, idx) => (
                <span
                    key={idx}
                    className="badge nav-pills border text-dark border border-secondary d-flex align-items-center"
                    style={{ cursor: "default" }}
                >
          {chip.label}
                    <span
                        style={{
                            marginLeft: 6,
                            cursor: "pointer",
                        }}
                        onClick={() => onRemove(chip.key)}
                    >
           <span className="fw-semibold fs-6"> ×</span>
          </span>
        </span>
            ))}
        </div>
    );
}


const PollLibrary = () => {
    const { user, poolStore } = useContext(Context);
    const isSuspended = user.user?.tariff_plan === "SUSPENDED";

    const [loading, setLoading] = useState(false);
    const [showFilterMenu, setShowFilterMenu] = useState(false);

    // --- Смарт-фильтры (единое состояние) ---
    const [filters, setFilters] = useState({
        query: "",
        types: new Set(), // COVER_POLL, TITLE_POLL, ...
        langs: new Set(), // "English", "Spanish", ...
        // time: [0, 120],   // (на будущее)
        // virt: [0, 300],   // (на будущее)
        // onlyWithImages: false,
        // newToday: false,
        sort: "newest", // "newest" | "earnings" | "time"
        page: 1,
        limit: 48,
    });

    const [mobileDraft, setMobileDraft] = useState(null);

    const openMobileFilters = () => {
        setMobileDraft(cloneFilters(filters));   // снимаем слепок
        setShowFilterMenu(true);
    };

    // хендлеры для панели
    const resetAndApply = () => {
        const clean = {
            query: "",
            types: new Set(),
            langs: new Set(),
            sort: "newest",
            page: 1,
            limit: 48,
        };
        setMobileDraft(clean);
        setFilters(f => ({ ...f, ...clean }));
        setShowFilterMenu(false);
    };

    const applyAndClose = () => {
        if (!mobileDraft) return;
        setFilters(f => ({ ...f, ...mobileDraft, page: 1 }));
        setShowFilterMenu(false);
    };

    const closeWithoutApply = () => setShowFilterMenu(false);

    const languages = [
        { id: 1, name: "English" },
        { id: 2, name: "Spanish" },
        { id: 3, name: "German" },
    ];

    const pollTypes = [
        { id: 1, name: "Cover Poll", type: "COVER_POLL" },
        { id: 2, name: "Cover&Title Poll", type: "COVER_TITLE_POLL" },
        { id: 3, name: "Title Poll", type: "TITLE_POLL" },
        { id: 4, name: "Description Poll", type: "DESCRIPTION_POLL" },
        { id: 5, name: "Mixed Poll", type: "MIXED_POLL" },
    ];

    // ===== Frontend sorting (always DESC) =====
    const sortRows = (rows, sortKey) => {
        if (!Array.isArray(rows) || rows.length === 0) return rows;

        // безопасные геттеры
        const getEarnings = (r) =>
            Number(
                r.pool?.default_price ??
                r.default_price ??
                r.earnings ??
                0
            ) || 0;

        const getTimeToAnswer = (r) =>
            Number(
                r.time_to_answer ??
                r.time_to_answer_minutes ??
                0
            ) || 0;

        const getCreatedMs = (r) => {
            const d =
                r.pool?.createdAt ??
                r.createdAt ??
                r.pool?.created_at ??
                r.created_at ??
                null;
            const ms = d ? new Date(d).getTime() : 0;
            return Number.isFinite(ms) ? ms : 0;
        };

        // общая схема: b - a → убывание (DESC)
        switch (sortKey) {
            case "earnings":
                return [...rows].sort((a, b) => {
                    const bv = getEarnings(b);
                    const av = getEarnings(a);
                    if (bv !== av) return bv - av;                 // по выплате
                    const bt = getTimeToAnswer(b), at = getTimeToAnswer(a);
                    if (bt !== at) return bt - at;                 // тай-брейк: время
                    return getCreatedMs(b) - getCreatedMs(a);      // тай-брейк: новизна
                });

            case "time":
                return [...rows].sort((a, b) => {
                    const bt = getTimeToAnswer(b);
                    const at = getTimeToAnswer(a);
                    if (bt !== at) return bt - at;                 // по времени
                    const bv = getEarnings(b), av = getEarnings(a);
                    if (bv !== av) return bv - av;                 // тай-брейк: выплата
                    return getCreatedMs(b) - getCreatedMs(a);      // тай-брейк: новизна
                });

            case "newest":
            default:
                return [...rows].sort((a, b) => {
                    const bd = getCreatedMs(b);
                    const ad = getCreatedMs(a);
                    if (bd !== ad) return bd - ad;                 // по дате
                    const bv = getEarnings(b), av = getEarnings(a);
                    if (bv !== av) return bv - av;                 // тай-брейк: выплата
                    return getTimeToAnswer(b) - getTimeToAnswer(a);// тай-брейк: время
                });
        }
    };


    // --- Автоприменение (debounce + отмена предыдущего запроса) ---
    const abortRef = useRef(null);

    const fetchData = async (nextFilters) => {
        abortRef.current?.abort?.();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        try {
            // Совместимость с текущим API:
            // language: берем один язык если выбран ровно один, иначе null
            const languageParam =
                nextFilters.langs.size === 1 ? [...nextFilters.langs][0] : null;

            // pollType: берем один тип если выбран ровно один, иначе null
            const pollTypeParam =
                nextFilters.types.size === 1 ? [...nextFilters.types][0] : null;

            const data = await getPollItemsLibrary(
                user?.user?.id,
                languageParam,
                pollTypeParam,
                nextFilters.limit,
                nextFilters.page,
                // объект с доп. фильтрами можно передавать при расширении API:
                // {
                //   query: nextFilters.query,
                //   time: nextFilters.time,
                //   virt: nextFilters.virt,
                //   onlyWithImages: nextFilters.onlyWithImages,
                //   newToday: nextFilters.newToday,
                //   sort: nextFilters.sort
                // },
                { signal: ctrl.signal }
            );
            // сортируем на фронте в соответствии с filters.sort
            const sortedRows = sortRows(data?.rows || [], nextFilters.sort);

            // записываем уже отсортированные
            poolStore.setPoolItems(sortedRows);
            poolStore.setTotalCount(data.count);
            poolStore.setPollLimit(nextFilters.limit);

            // если бек не считает страницы сам — ничего не делаем
        } catch (e) {
            if (e?.name !== "AbortError") {
                console.error("Fetch error:", e);
            }
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetch = useMemo(
        () => debounce(fetchData, 450),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        debouncedFetch(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, user?.user?.id]);

    const toggleType = (type) =>
        setFilters((f) => ({ ...f, types: toggleInSet(f.types, type), page: 1 }));

    const toggleLang = (lang) =>
        setFilters((f) => ({ ...f, langs: toggleInSet(f.langs, lang), page: 1 }));

    const setSort = (val) => setFilters((f) => ({ ...f, sort: val, page: 1 }));

    const setLimit = (val) => setFilters((f) => ({ ...f, limit: val, page: 1 }));

    const setPage = (p) => setFilters((f) => ({ ...f, page: p }));

    const clearAll = () =>
        setFilters({
            query: "",
            types: new Set(),
            langs: new Set(),
            sort: "newest",
            page: 1,
            limit: 48,
        });

    const removeFilterKey = (key) => {
        // точное совпадение для простых ключей
        if (key === "query") {
            setFilters(f => ({ ...f, query: "", page: 1 }));
            return;
        }
        if (key === "sort") {
            // на случай если решишь использовать просто "sort"
            setFilters(f => ({ ...f, sort: "newest", page: 1 }));
            return;
        }

        // составные ключи вида "type:<VALUE>", "lang:<VALUE>", "sort:<VALUE>"
        const [kind, value] = key.split(":");
        if (kind === "type" && value) {
            setFilters(f => {
                const next = new Set(f.types);
                next.delete(value);
                return { ...f, types: next, page: 1 };
            });
            return;
        }
        if (kind === "lang" && value) {
            setFilters(f => {
                const next = new Set(f.langs);
                next.delete(value);
                return { ...f, langs: next, page: 1 };
            });
            return;
        }
        if (kind === "sort") {
            // клик по "Sort: earnings" → вернуть дефолт
            setFilters(f => ({ ...f, sort: "newest", page: 1 }));
            return;
        }

        // совместимость: если придёт "types" или "langs" — очистить всё множество
        if (key === "types") {
            setFilters(f => ({ ...f, types: new Set(), page: 1 }));
        } else if (key === "langs") {
            setFilters(f => ({ ...f, langs: new Set(), page: 1 }));
        }
    };

    if (isSuspended) {
        return (
            <div className="container-fluid px-3" style={{ marginTop: "-10px" }}>
                <AccountSuspendedAlert />
            </div>
        );
    }

    return (
        <div
            className="container-fluid px-3"
            style={{ overflowX: "hidden", marginTop: "-10px" }}
        >
            <SEO
                url="https://virtalibry.com"
                canonical="https://virtalibry.com"
                organization={{
                    name: "Virtalibry",
                    url: "https://virtalibry.com",
                    logo: "https://virtalibry.com/face/why_love_is_gold_new.png",
                    sameAs: ["https://twitter.com/virtalibry"],
                    contactPoint: [
                        {
                            "@type": "ContactPoint",
                            contactType: "customer support",
                            email: "info@virtalibry.com",
                        },
                    ],
                }}
                breadcrumb={[
                    { name: "Virtalibry Home", item: "https://virtalibry.com" },
                    { name: "Poll Library", item: "https://virtalibry.com/survey_library" },
                ]}
            />

            {/* Заголовок */}
            <Row>
                <Col xs={12} className="ms-0">
                    <h1 className="fs-6"></h1>
                </Col>
            </Row>

            {/* Мобильная панель кнопок */}
            <div className="d-flex d-md-none justify-content-between align-items-center mb-2 px-1">
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => clearAll()}
                >
                    <i className="bi bi-x-circle me-1"></i> Reset
                </Button>
                <Button
                    variant="outline-success"
                    size="sm"
                    onClick={openMobileFilters}
                >
                    <i className="bi bi-funnel-fill me-2"></i> Filters
                </Button>

            </div>

            <Row className="g-3">
                {/* --- Сайдбар (md=3), sticky --- */}
                {/* --- Сайдбар фильтров (md=3), sticky --- */}
                <Col md={3} className="d-none d-md-block">
                    <div
                        className="p-3 border border-secondary rounded"
                        style={{ position: "sticky", top: 72, zIndex: 1, background: "#fff" }}
                    >
                        {/* Заголовок раздела */}
                        <div className="mb-3">
                            <h6 className="fw-semibold text-muted">Poll Library</h6>
                            <p className="text-muted small mb-1">
                                Here you can browse the available polls, take part in one, and once it’s
                                completed your participation will be rewarded.
                            </p>
                            <p className="text-muted small mb-1">
                                Use the filters on the left to narrow down the list and find the polls that
                                suit you best. You can experiment with options freely and reset everything with a single click.
                            </p>
                        </div>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="text-muted small">
                                Found: <strong>{poolStore?.totalCount ?? 0} </strong>record(s).
                            </div>
                        </div>

                        <div className="mb-3">
                            <Form.Label className="small text-muted">Set Poll Type Filters:</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                                {pollTypes.map((p) => {
                                    const active = filters.types.has(p.type);
                                    return (
                                        <Button
                                            key={p.id}
                                            size="sm"
                                            variant={active ? "success" : "outline-success"}
                                            onClick={() => toggleType(p.type)}
                                        >
                                            {p.name}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mb-3">
                            <Form.Label className="small text-muted">Set Language Filters:</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                                {languages.map((l) => {
                                    const active = filters.langs.has(l.name);
                                    return (
                                        <Button
                                            key={l.id}
                                            size="sm"
                                            variant={active ? "success" : "outline-success"}
                                            onClick={() => toggleLang(l.name)}
                                        >
                                            {l.name}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mb-3">
                            <Row>
                                <Col>
                                    <Form.Label className="small text-muted">Sort Polls by:</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={filters.sort}
                                        onChange={(e) => setSort(e.target.value)}
                                    >
                                        <option value="newest">Newest first</option>
                                        <option value="earnings">Highest earnings first</option>
                                        <option value="time">Longest time first</option>
                                    </Form.Select>
                                </Col>
                                <Col>
                                    <Form.Label className="small text-muted">Records Per Page:</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={filters.limit}
                                        onChange={(e) => setLimit(Number(e.target.value))}
                                    >
                                        <option value={12}>12</option>
                                        <option value={24}>24</option>
                                        <option value={48}>48</option>
                                        <option value={96}>96</option>
                                    </Form.Select>
                                </Col>
                            </Row>


                        </div>

                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={clearAll}>
                                Reset All Filters
                            </Button>
                        </div>
                    </div>
                </Col>


                {/* --- Контент (md=9): чипы + сетка карточек --- */}
                <Col xs={12} md={9}>
                    <ActiveFilterChips
                        filters={filters}
                        onClear={clearAll}
                        onRemove={removeFilterKey}
                    />

                    {/* Сетка: 3 колонки на lg (2 на md, 4 на xl) */}
                    {loading ? (
                        <Loader />
                    ) : (
                        <>
                            <Row className="g-0 d-flex justify-content-start align-items-start flex-wrap mt-1 mb-2">
                                {(poolStore?.poolitems || []).map((b) => (
                                    <PollLibraryItem key={b.id} pollitem={b} />
                                ))}
                            </Row>

                            {poolStore?.totalCount === 0 && (
                                <Row className="justify-content-center mt-2">
                                    <Col md={8}>
                                        <div className="card border-secondary text-center">
                                            <div className="card-body">
                                                <h6 className="text-muted mb-0">
                                                    Unfortunately, there are no active polls matching your
                                                    selected criteria at the moment. Please try adjusting the
                                                    filters or check back later.
                                                </h6>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                            )}

                            {poolStore?.totalCount > 0 && (
                                <Row>
                                        <PagesPolls
                                        onChangePage={(p) => setPage(p)}
                                        // Если ваш PagesPolls сам живёт от store — этот проп опционален
                                    />
                                </Row>
                            )}
                        </>
                    )}
                </Col>
            </Row>

            {/* Мобильная панель */}
            <MobileFilterPanel
                show={showFilterMenu}
                mobileDraft={mobileDraft}
                setMobileDraft={setMobileDraft}
                pollTypes={pollTypes}
                languages={languages}
                toggleInSet={toggleInSet}
                onClose={closeWithoutApply}
                onApply={applyAndClose}
                onResetApply={resetAndApply}
            />
        </div>
    );
};

export default observer(PollLibrary);
